import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Modal, ModalBody} from "reactstrap";
import CustomModalHeader from "./CustomModalHeader";
import PropTypes from "prop-types";
import HttpRequest from "./HttpRequest";
import '@jumio/websdk'
import {useTranslation} from "react-i18next";

const jumioThemeStyles = {
    '--jumio-sdk-font': "'Roboto', sans-serif",
    '--jumio-sdk-color-primary1': 'hsl(199, 100%, 90%)',
    '--jumio-sdk-color-primary2': 'hsl(199, 100%, 70%)',
    '--jumio-sdk-color-primary3': 'hsl(199, 100%, 60%)',
    '--jumio-sdk-color-primary4': 'hsl(199, 100%, 50%)',
    '--jumio-sdk-color-primary5': 'hsl(199, 100%, 40%)',
    '--jumio-sdk-color-primary6': 'hsl(199, 100%, 30%)',
    '--jumio-sdk-color-primary7': 'hsl(199, 100%, 10%)',
    '--jumio-sdk-color-secondary1': 'hsl(0, 0%, 97%)',
    '--jumio-sdk-color-secondary2': 'hsl(0, 0%, 95%)',
    '--jumio-sdk-color-secondary3': 'hsl(0, 0%, 90%)',
    '--jumio-sdk-color-secondary4': 'hsl(0, 0%, 81%)',
    '--jumio-sdk-color-secondary5': 'hsl(0, 0%, 65%)',
    '--jumio-sdk-color-secondary6': 'hsl(0, 0%, 50%)',
    '--jumio-sdk-color-secondary7': 'hsl(0, 0%, 40%)',
    '--jumio-sdk-color-secondary8': 'hsl(0, 0%, 28%)',
    '--jumio-sdk-color-secondary9': 'hsl(0, 0%, 24%)',
    '--jumio-sdk-color-secondary10': 'hsl(0, 0%, 20%)',
    '--jumio-sdk-color-secondary11': 'hsl(0, 0%, 16%)',
    '--jumio-sdk-color-secondary12': 'hsl(0, 0%, 14%)',
    width: '100%',
    height: '100%',
    'min-width': '100%',
    'min-height': '100%'
};

export default function IdentityVerificationModal({isOpen, boxId, onDone, onCancel}) {

    const {t, i18n} = useTranslation()
    const [url, setUrl] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [match, setMatch] = useState(null);
    const [error, setError] = useState(null);
    const jumioRef = useRef(null);

    const receiveMessage = useCallback((event) => {
        if (!['https://firmamex-bankingly-test.web.amer-1.jumio.ai',
            'https://firmamex-bankingly-prod.web.amer-1.jumio.ai'
        ].includes(event.origin)) {
            return;
        }
        const data = window.JSON.parse(event.data);
        const payload = data.payload;
        if (payload) {
            if (payload.value === 'success' || payload.value === 'error') {
                onDone({
                    requestId
                })
            }
        }
    }, [requestId])

    useEffect(() => {
        window.addEventListener("message", receiveMessage, false);
        return function () {
            window.removeEventListener("message", receiveMessage, false);
        }
    }, [receiveMessage]);


    useEffect(() => {

        async function requestVerification() {
            try {

                const lang = i18n.language ?? 'es'
                const res = await HttpRequest.post(`/browser/kyc/verification`, {
                    signatureBoxId: boxId,
                    lang: lang.toLowerCase()
                });
                if (res.status === 200) {
                    if (res.data.status !== 'SUCCESS') {
                        setRequestId(res.data.requestVerificationId)
                        setUrl(res.data.url)
                        if (res.data.match && Object.keys(res.data.match).length > 0) {
                            setMatch(Object.entries(res.data.match).map(([k, v]) => ({
                                key: k,
                                value: v
                            })));
                        } else {
                            setMatch(null)
                        }
                    } else if (res.data.status === 'SUCCESS') {
                        onDone({
                            requestId: res.data.requestVerificationId
                        })
                    }
                }
            } catch (e) {
                if (e?.response?.data?.error) {
                    const message = e.response?.data?.error;
                    if (message === 'identity verification response not available') {
                        setError(message);
                    }
                }
            }
        }

        if (isOpen && boxId) {
            requestVerification();
        } else {
            setUrl(null)
            setRequestId(null)
            setMatch(null)
        }
    }, [isOpen, boxId, onDone]);


    function handleClickCancel() {
        onCancel();
    }

    return (
        <Modal isOpen={isOpen} className={"identity-verification-modal"}>
            <CustomModalHeader
                toggle={handleClickCancel}
            >
                {t('identity verification')}
            </CustomModalHeader>
            <ModalBody>
                {
                    error ?
                        <div className={'text-center w-50 m-auto'}>
                            <h3>{t('identity verification error no available title')}</h3>
                            <br/>
                            <p>
                                {t('identity verification error no available body')}
                            </p>
                        </div> : null
                }

                {
                    url  ?
                        <iframe
                            title="identityIframe"
                            allow="camera;autoplay;fullscreen;clipboard-read;clipboard-write;accelerometer;gyroscope;magnetometer"
                            width="70%" height="80%"
                            allowFullScreen
                            className="identity-verification-modal-iframe"
                            style={{position: 'absolute', width: '100%', top: 0, left: 0, border: 'none'}}
                            src={url}>
                        </iframe>
                        : null
                }
                {
                    match ?
                        <div style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'white'
                        }}>
                            <div
                                style={{
                                    margin: 'auto',
                                    maxWidth: '500px',
                                    padding: '25px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    fontWeight: 'bold',
                                }}
                            >
                                <p>
                                    Estás pasando a un proceso de verificación de identidad el cual solicita datos específicos.
                                    Asegúrate que los datos en tu identificación oficial sean estos mismos:
                                </p>
                                <ol>
                                    {
                                        match.map(item => (
                                            <li key={item.key}>
                                                <span>{ item.key } - { item.value }</span>
                                            </li>
                                        ))
                                    }
                                </ol>
                                <Button color="secondary" onClick={() => setMatch(null)}>
                                    {t('accept')}
                                </Button>
                            </div>
                        </div> : null
                }
            </ModalBody>
        </Modal>
    )

}

IdentityVerificationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    boxId: PropTypes.number.isRequired,
    onDone: PropTypes.func.isRequired
}
